import typeOf from 'just-typeof';

export function useUserPermissions() {
    const { user } = useUserSession();
    const { urlParams } = useAdminUrl();

    /**
     * Get the product access object for a specific product
     *
     * @param {string} product
     * @return {Object}
     */
    const getProductAccess = (product) => {
        if (!user.value?.productAccess || !product) {
            return {};
        }

        return user.value.productAccess[product] ?? {};
    };

    // Returns an array of users capabilities
    const userCapabilities = computed(() => {
        if (!user.value) {
            return {};
        }
        // TODO
        return {};
    });

    /**
     * Returns the current product access based on the URL parameters or last used product
     *
     * @return {Object} The product access object for the current product
     */
    const currentProductAccess = computed(() => {
        if (!user.value || !urlParams.value.product) {
            return {};
        }

        const productUuid =
            urlParams.value.product ?? user.value.lastUsedProduct;
        return getProductAccess(productUuid);
    });

    // Returns an array of user roles
    const userRoles = computed(() => {
        return currentProductAccess.value.roles ?? [];
    });

    // Returns an array of products that the user can manage
    const manageableProducts = computed(() => {
        return Object.entries(user.value?.productAccess ?? {})
            .filter(([, value]) => value.canManage)
            .map(([key]) => key);
    });

    // Returns a boolean value if the user can manage products
    const canManageProducts = computed(() => {
        if (!user.value) {
            return false;
        }

        return manageableProducts.value.length > 0 || false;
    });

    // Returns a boolean value if the user has frontend management access
    const hasFrontendManagementAccess = computed(() => {
        if (!user.value) {
            return false;
        }

        return user.value.frontendManagementAccess || false;
    });

    /**
     * Check if the user has access to a specific product
     *
     * @param {string} productUuid
     * @return {boolean}
     */
    const checkProductAccess = (productUuid) => {
        if (!productUuid || !user.value) {
            return false;
        }

        const productAccess = getProductAccess(productUuid);

        return productAccess.canManage ?? false;
    };

    /**
     * Check if the user has access to the current product
     *
     * @return {boolean}
     */
    const canAccessCurrentProduct = computed(() => {
        if (!user.value) {
            return false;
        }

        return checkProductAccess(urlParams.value.product);
    });

    /**
     * Check if the user has access to the product management of a specific product
     * @param {string} product
     * @return {boolean}
     */
    const checkProductManagementAccess = (product) => {
        if (!user.value) {
            return false;
        }

        const productAccess = getProductAccess(product);

        return productAccess.canManage ?? false;
    };

    /**
     * Check if the user has a specific role
     *
     * @param {string|array} roles
     * @return {boolean}
     */
    const hasRole = (roles) => {
        // If user is not logged in, return false
        if (!user.value) {
            return false;
        }

        // If roles is a string, convert it to an array
        if (typeOf(roles) === 'string') {
            roles = roles.split('|');
        }

        return userRoles.value.some((role) => roles.includes(role));
    };

    /**
     * Check if the user does not have a specific role
     *
     * @param {string|array} roles
     * @return {boolean}
     */
    const hasNotRole = (roles) => {
        return !hasRole(roles);
    };

    /**
     * Check if the user has a specific capability
     *
     * @param {string} capability
     * @return {boolean}
     */
    const hasCapability = (capability) => {
        console.warn('hasCapability is not implemented yet');
        // TODO implement
        return false;
    };

    /**
     * Check if the user does not have a specific capability
     *
     * @param {string} capability
     * @return {boolean}
     */
    const hasNotCapability = (capability) => {
        console.warn('hasNotCapability is not implemented yet');
        return !hasCapability(capability);
    };

    /**
     * Check if the user has required roles or capabilities
     *
     * @param {string|array} rolesOrCapabilities required roles or capabilities
     * @returns boolean
     */
    const can = (rolesOrCapabilities) => {
        if (typeOf(rolesOrCapabilities) === 'string') {
            rolesOrCapabilities = rolesOrCapabilities.split('|');
        }

        const userRolesAndCapabilities = [
            ...userRoles.value,
            ...userCapabilities.value,
        ];

        return rolesOrCapabilities.some((roleOrCapability) =>
            userRolesAndCapabilities.includes(roleOrCapability),
        );
    };

    /**
     * Check if the user does not have required roles or capabilities
     *
     * @param {string|array}
     * @returns boolean
     */
    const cant = (rolesOrCapabilities) => {
        return !can(rolesOrCapabilities);
    };

    return {
        // Computed values
        userCapabilities,
        userRoles,
        manageableProducts,
        canManageProducts,
        canAccessCurrentProduct,
        hasFrontendManagementAccess,
        // Helper functions
        checkProductAccess,
        checkProductManagementAccess,
        hasRole,
        hasNotRole,
        hasCapability,
        hasNotCapability,
        // Helper function to merge hasRole and hasCapability
        can,
        cant,
    };
}
